import React, { useState } from 'react';
import { Search, Filter, Mail, MessageSquare, Phone, Sparkles, MessageCircle, Activity } from 'lucide-react';
import { activities } from './data';
import { ActivityCard } from '../../components/crm/activities/activity-card';

// Channel Filter Button Component
const ChannelButton: React.FC<{
  icon: React.ReactNode;
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}> = ({ icon, label, count, active, onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
      active
        ? 'bg-slate-900 text-white'
        : 'bg-white text-slate-700 hover:bg-slate-50 border border-slate-200'
    }`}
  >
    {icon}
    <span className="hidden sm:inline">{label}</span>
    <span className={`${active ? 'text-slate-300' : 'text-slate-500'}`}>({count})</span>
  </button>
);


// Main Component
const App: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<string>('all');

  const channelCounts = {
    all: activities.length,
    email: activities.filter(a => a.channel === 'email').length,
    whatsapp: activities.filter(a => a.channel === 'whatsapp').length,
    'web-chat': activities.filter(a => a.channel === 'web-chat').length,
    call: activities.filter(a => a.channel === 'call').length,
    'ai-call': activities.filter(a => a.channel === 'ai-call').length
  };

  const filteredActivities = activities.filter(activity => {
    const matchesSearch = 
      activity.customer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      activity.customer.company.toLowerCase().includes(searchQuery.toLowerCase()) ||
      activity.description.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = activeFilter === 'all' || activity.channel === activeFilter;
    
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center gap-3 mb-2">
           <Activity className='text-blue-600' size={40} />
            <h1 className="text-2xl sm:text-3xl lg:text-2xl font-bold text-slate-900">Recent Activities</h1>
          </div>
          <p className="text-slate-600 text-sm sm:text-base">Complete history of all customer engagements and interactions</p>
        </div>

        {/* Filters Section */}
        <div className="bg-white rounded-lg border border-slate-200 p-4 sm:p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-slate-700" />
            <h2 className="font-semibold text-slate-900">Filters</h2>
          </div>

          {/* Search */}
          <div className="mb-4">
            <div className="relative w-1/2">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search by customer name, company, or activity..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Channel Filters */}
          <div>
            <p className="text-sm font-medium text-slate-700 mb-3">Filter by Channel:</p>
            <div className="flex flex-wrap gap-2">
              <ChannelButton
                icon={null}
                label="All"
                count={channelCounts.all}
                active={activeFilter === 'all'}
                onClick={() => setActiveFilter('all')}
              />
              <ChannelButton
                icon={<Mail className="w-4 h-4" />}
                label="Email"
                count={channelCounts.email}
                active={activeFilter === 'email'}
                onClick={() => setActiveFilter('email')}
              />
              <ChannelButton
                icon={<MessageSquare className="w-4 h-4" />}
                label="WhatsApp"
                count={channelCounts.whatsapp}
                active={activeFilter === 'whatsapp'}
                onClick={() => setActiveFilter('whatsapp')}
              />
              <ChannelButton
                icon={<MessageCircle className="w-4 h-4" />}
                label="Web Chat"
                count={channelCounts['web-chat']}
                active={activeFilter === 'web-chat'}
                onClick={() => setActiveFilter('web-chat')}
              />
              <ChannelButton
                icon={<Phone className="w-4 h-4" />}
                label="Calls"
                count={channelCounts.call}
                active={activeFilter === 'call'}
                onClick={() => setActiveFilter('call')}
              />
              <ChannelButton
                icon={<Sparkles className="w-4 h-4" />}
                label="AI Calls"
                count={channelCounts['ai-call']}
                active={activeFilter === 'ai-call'}
                onClick={() => setActiveFilter('ai-call')}
              />
            </div>
          </div>
        </div>

        {/* Results Count */}
        <p className="text-slate-600 mb-4 text-sm sm:text-base">
          Showing <span className="font-semibold">{filteredActivities.length}</span> of{' '}
          <span className="font-semibold">{activities.length}</span> activities
        </p>

        {/* Activities List */}
        <div className="space-y-4 h-[600px] bg-white rounded-xl p-7 shadow-md overflow-auto">
          {filteredActivities.map((activity) => (
            <ActivityCard key={activity.id} activity={activity} />
          ))}
        </div>

        {/* Empty State */}
        {filteredActivities.length === 0 && (
          <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 text-slate-300">
              <Search className="w-full h-full" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No activities found</h3>
            <p className="text-slate-600">Try adjusting your search or filters</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default App;